import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { loadToken } from '../../localStorage';
import {
  Paper,
  makeStyles,
  TableBody,
  TableRow,
  TableCell,
  Toolbar,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Box,
} from '@material-ui/core';
import useTable from '../../components/useTable';
import Controls from '../../components/controls/Controls';
import { Search } from '@material-ui/icons';
import AddIcon from '@material-ui/icons/Add';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import Popup from '../../components/Popup';
import CloseIcon from '@material-ui/icons/Close';
import Notification from '../../components/Notification';
import ConfirmDialog from '../../components/ConfirmDialog';
import { removeToken, checkUTF8 } from '../../utils/subscriptionsService';
import {
  getAppData,
  addResourceToAppData,
  updateResourceInAppData,
  removeResourceFromAppData,
} from '../../utils/appDataService';
import Copyright from '../../components/Copyright';
import ResourcesForm from './ResourcesForm';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiCircularProgress-root': {
      'margin-left': '50%',
      marginTop: '20px',
      width: '100%',
    },
  },
  pageContent: {
    margin: theme.spacing(5),
    padding: theme.spacing(3),
  },
  searchInput: {
    width: '75%',
  },
  newButton: {
    position: 'absolute',
    right: '10px',
  },
}));

const headCells = [
  { id: 'title', label: 'Nom' },
  { id: 'idRessource', label: 'Id ressource' },
  { id: 'subscriptionCount', label: 'Abonnements' },
  { id: 'actions', label: 'Actions', disableSorting: true },
];

export default function Resources() {
  const classes = useStyles();
  const history = useHistory();
  const token = loadToken();

  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filterFn, setFilterFn] = useState({
    fn: (items) => items,
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: '',
    type: '',
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getAppData({ token, history });
        setResources(data.resources);
        setSubscriptions(data.subscriptions);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, history]);

  const getSubscriptionCount = (idRessource) =>
    subscriptions.filter((subscription) => subscription.idRessource === idRessource).length;

  const putData = async (values) => {
    const res = await fetch(`${process.env.REACT_APP_URL_API}/resources`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });

    const data = await res.json();
    const { status, msg } = data;
    if (!res.ok) {
      setNotify({
        isOpen: true,
        message: msg || 'La création de la ressource a échoué',
        type: status || 'error',
      });
      removeToken(res, history);
      return false;
    }

    setNotify({
      isOpen: true,
      message: 'Soumis avec succès',
      type: 'success',
    });

    const newResource = {
      ...data,
      subscriptionCount: 0,
    };
    addResourceToAppData(newResource);
    setResources((currentResources) => currentResources.concat(newResource));
    removeToken(res, history);
    return true;
  };

  const patchData = async (resourceId, values) => {
    const res = await fetch(
      `${process.env.REACT_APP_URL_API}/resources/${encodeURIComponent(resourceId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      },
    );

    const data = await res.json();
    const { status, msg } = data;
    if (!res.ok) {
      setNotify({
        isOpen: true,
        message: msg || 'La modification de la ressource a échoué',
        type: status || 'error',
      });
      removeToken(res, history);
      return false;
    }

    setNotify({
      isOpen: true,
      message: 'Soumis avec succès',
      type: 'success',
    });

    updateResourceInAppData(resourceId, data.resource);
    setResources((currentResources) =>
      currentResources.map((resource) =>
        resource._id === resourceId ? { ...resource, ...data.resource } : resource,
      ),
    );
    removeToken(res, history);
    return true;
  };

  const addOrEdit = async (resource, resetForm) => {
    let isSaved = false;
    if (!recordForEdit) {
      isSaved = await putData(resource);
    } else {
      isSaved = await patchData(recordForEdit._id, { title: resource.title });
    }

    if (!isSaved) return;

    resetForm();
    setRecordForEdit(null);
    setOpenPopup(false);
  };

  const openInPopup = (item) => {
    setRecordForEdit(item);
    setOpenPopup(true);
  };

  const deleteData = async (resourceId) => {
    const res = await fetch(
      `${process.env.REACT_APP_URL_API}/resources/${encodeURIComponent(resourceId)}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (res.status === 204) {
      setConfirmDialog({
        ...confirmDialog,
        isOpen: false,
      });
      removeResourceFromAppData(resourceId);
      setResources((currentResources) =>
        currentResources.filter((resource) => resource._id !== resourceId),
      );
      setNotify({
        isOpen: true,
        message: 'Ressource supprimée avec succès',
        type: 'success',
      });
    } else {
      const data = await res.json();
      setNotify({
        isOpen: true,
        message: data.msg,
        type: data.status,
      });
    }
  };

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } = useTable(
    resources.map((resource) => ({
      ...resource,
      subscriptionCount: getSubscriptionCount(resource.idRessource),
    })),
    headCells,
    filterFn,
  );

  const handleSearch = (e) => {
    const { value } = e.target;
    setFilterFn({
      fn: (items) => {
        if (value === '') return items;
        return items.filter(
          (item) =>
            checkUTF8(item.title).toLowerCase().includes(value.toLowerCase()) ||
            item.idRessource.toLowerCase().includes(value.toLowerCase()),
        );
      },
    });
  };

  const handleDelete = (item) => {
    const subscriptionCount = item.subscriptionCount || 0;
    if (subscriptionCount > 0) {
      setNotify({
        isOpen: true,
        message: `Suppression impossible : cette ressource est liée à ${subscriptionCount} abonnement(s)`,
        type: 'warning',
      });
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Êtes-vous sûr de vouloir supprimer cette ressource ?',
      subTitle: 'Vous ne pouvez pas annuler cette opération',
      onConfirm: () => {
        deleteData(item._id);
      },
    });
  };

  return (
    <>
      <Paper className={classes.pageContent}>
        <Toolbar>
          <Controls.Input
            label='Recherche de ressource'
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
            onChange={handleSearch}
          />
          <Controls.Button
            text='Ajouter une ressource'
            variant='outlined'
            startIcon={<AddIcon />}
            className={classes.newButton}
            onClick={() => {
              setRecordForEdit(null);
              setOpenPopup(true);
            }}
          />
        </Toolbar>
        {loading ? (
          <div className={classes.root}>
            <CircularProgress />
          </div>
        ) : (
          <TblContainer>
            <TblHead />
            <TableBody>
              {recordsAfterPagingAndSorting().map((item) => (
                <TableRow key={item._id}>
                  <Tooltip title={item.title}>
                    <TableCell>{checkUTF8(item.title)}</TableCell>
                  </Tooltip>
                  <TableCell>{checkUTF8(item.idRessource)}</TableCell>
                  <TableCell>{item.subscriptionCount}</TableCell>
                  <TableCell>
                    <Controls.ActionButton
                      color='primary'
                      onClick={() => openInPopup(item)}
                    >
                      <EditOutlinedIcon fontSize='small' />
                    </Controls.ActionButton>
                    <Controls.ActionButton
                      color='secondary'
                      onClick={() => handleDelete(item)}
                    >
                      <CloseIcon fontSize='small' />
                    </Controls.ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </TblContainer>
        )}
        <TblPagination />
      </Paper>
      <Box mt={8}>
        <Copyright />
      </Box>
      <Popup
        title={recordForEdit ? 'Modifier une ressource' : 'Ajouter une ressource'}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <ResourcesForm addOrEdit={addOrEdit} recordForEdit={recordForEdit} />
      </Popup>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </>
  );
}
